/**
 * MenuImportModal — import menu z pliku eksportu (przeniesienie menu z innego tenanta).
 *
 * Kroki:
 *  1. 'pick'    — wybór pliku JSON (przeciągnij albo „Wybierz plik"); plik czytany w przeglądarce
 *                 i od razu wysyłany do podglądu.
 *  2. 'preview' — raport serwera: co wczytamy, co bez przypiętego układu, czego nie wczytamy
 *                 i dlaczego + nazwa nowego menu. „Importuj" zapisuje.
 *
 * Backend: POST /api/menu/layouts/import?dryRun=true (podgląd) | bez dryRun (zapis, 201).
 * Ocena pozycji zawsze po stronie serwera (dane tego tenanta) — plik jest niezaufany.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  MENU_IMPORT_MAX_BYTES,
  type MenuImportPreviewResponse,
  type MenuImportReportEntry,
  type MenuImportSkipReason,
  type MenuItem,
} from '@saas/shared';
import { useMutation } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { Eye, FileJson } from 'lucide-react';

import { Alert } from '@/components/Alert';
import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';

const P = 'tenant.menuLayoutBuilder.import';

interface ImportedLayout {
  id: string;
  name: string;
}

export interface MenuImportModalProps {
  open: boolean;
  onClose: () => void;
  /** Wywoływane po zapisie — rodzic odświeża listę i otwiera nowe menu. */
  onImported: (layoutId: string) => void;
  /** „Podgląd menu" przed zapisem — rodzic otwiera okno podglądu z tym drzewem. */
  onPreview?: (menu: { name: string; hierarchy: MenuItem[] }) => void;
}

type Step = 'pick' | 'preview';

function errorCode(err: unknown): string | null {
  if (isAxiosError(err)) {
    const data = err.response?.data as { error?: string } | undefined;
    if (data?.error) return data.error;
    // Odcięcie przez proxy (Nginx) przychodzi bez JSON-a — rozpoznajemy po statusie.
    if (err.response?.status === 413) return 'PAYLOAD_TOO_LARGE';
    if (err.response?.status === 403) return 'PERMISSION_DENIED';
    return null;
  }
  return null;
}

export function MenuImportModal({ open, onClose, onImported, onPreview }: MenuImportModalProps) {
  const { t } = useTranslation();
  const toast = useToast();

  const [step, setStep] = useState<Step>('pick');
  const [fileName, setFileName] = useState<string | null>(null);
  const [fileContent, setFileContent] = useState<unknown>(null);
  const [preview, setPreview] = useState<MenuImportPreviewResponse | null>(null);
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const errorMessage = (code: string | null): string => {
    switch (code) {
      case 'INVALID_FORMAT':
        return t(`${P}.errors.invalidFormat`);
      case 'UNSUPPORTED_FORMAT_VERSION':
        return t(`${P}.errors.unsupportedVersion`);
      case 'INVALID_FILE':
        return t(`${P}.errors.invalidFile`);
      case 'PAYLOAD_TOO_LARGE':
        return t(`${P}.errors.tooLarge`);
      case 'PERMISSION_DENIED':
        return t(`${P}.errors.permissionDenied`);
      case 'NOTHING_TO_IMPORT':
        return t(`${P}.errors.nothingToImport`);
      default:
        return t(`${P}.errors.generic`);
    }
  };

  function reset() {
    setStep('pick');
    setFileName(null);
    setFileContent(null);
    setPreview(null);
    setName('');
    setNameError(null);
    setFileError(null);
    setIsDragOver(false);
  }

  const previewMutation = useMutation({
    mutationFn: async (content: unknown) =>
      (
        await api.post<MenuImportPreviewResponse>('/menu/layouts/import?dryRun=true', {
          file: content,
        })
      ).data,
    onSuccess: (data) => {
      setPreview(data);
      setName(data.proposedName);
      setStep('preview');
    },
    onError: (err) => setFileError(errorMessage(errorCode(err))),
  });

  const importMutation = useMutation({
    mutationFn: async () =>
      (
        await api.post<{ layout: ImportedLayout; renamed?: boolean }>('/menu/layouts/import', {
          file: fileContent,
          name: name.trim(),
        })
      ).data,
    onSuccess: ({ layout, renamed }) => {
      // Zajęta nazwa nie blokuje zapisu — serwer nadaje wolną i mówimy, jaką.
      toast.show({
        variant: 'success',
        title: t(`${P}.toasts.imported`, { name: layout.name }),
        description: renamed
          ? `${t(`${P}.toasts.importedRenamed`, { requested: name.trim() })} ${t(`${P}.toasts.importedHint`)}`
          : t(`${P}.toasts.importedHint`),
      });
      reset();
      onImported(layout.id);
      onClose();
    },
    onError: (err) => {
      const code = errorCode(err);
      if (code === 'NAME_TAKEN') {
        setNameError(t(`${P}.errors.nameTaken`));
        return;
      }
      toast.show({ variant: 'error', title: errorMessage(code) });
    },
  });

  const running = previewMutation.isPending || importMutation.isPending;

  function handleClose() {
    if (running) return;
    reset();
    onClose();
  }

  async function pickFile(file: File | null) {
    // W trakcie sprawdzania pliku ignorujemy kolejny — raport i zapis muszą dotyczyć tego samego.
    if (!file || running) return;
    setFileError(null);
    setFileName(file.name);
    // Za duży plik odrzucamy przed odczytem (ten sam limit co serwer) — nie zawiesza karty.
    if (file.size > MENU_IMPORT_MAX_BYTES) {
      setFileError(t(`${P}.errors.tooLarge`));
      return;
    }
    let parsed: unknown;
    try {
      parsed = JSON.parse(await file.text());
    } catch {
      setFileError(t(`${P}.errors.notJson`));
      return;
    }
    setFileContent(parsed);
    previewMutation.mutate(parsed);
  }

  const reasonText = (reason: MenuImportSkipReason | undefined): string =>
    reason ? t(`${P}.reasons.${reason}`) : '';

  const entryLabel = (e: MenuImportReportEntry): string =>
    e.path.length > 0 ? `${e.path.join(' › ')} › ${e.label}` : e.label;

  const skipped = preview?.report.entries.filter((e) => e.status === 'skipped') ?? [];
  const withoutLayout =
    preview?.report.entries.filter((e) => e.status === 'loaded_without_layout') ?? [];
  const loaded = preview?.report.entries.filter((e) => e.status === 'loaded') ?? [];

  const footer =
    step === 'preview' ? (
      <>
        {onPreview && preview?.importable ? (
          <Button
            variant="secondary"
            leftIcon={<Eye className="h-4 w-4" />}
            onClick={() =>
              onPreview({
                name: name.trim() || preview.proposedName,
                hierarchy: preview.hierarchy,
              })
            }
            disabled={running}
            data-testid="menu-import-preview"
          >
            {t(`${P}.preview`)}
          </Button>
        ) : null}
        <Button variant="ghost" onClick={reset} disabled={running} data-testid="menu-import-back">
          {t(`${P}.chooseAnother`)}
        </Button>
        <Button
          variant="primary"
          onClick={() => {
            setNameError(null);
            importMutation.mutate();
          }}
          loading={importMutation.isPending}
          disabled={!preview?.importable || name.trim().length === 0 || running}
          data-testid="menu-import-submit"
        >
          {t(`${P}.submit`)}
        </Button>
      </>
    ) : (
      <Button variant="ghost" onClick={handleClose} disabled={running}>
        {t('common.cancel')}
      </Button>
    );

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={t(`${P}.title`)}
      description={t(`${P}.description`)}
      size="lg"
      footer={footer}
      closeOnBackdrop={!running}
      closeOnEsc={!running}
      testId="menu-import-modal"
    >
      {step === 'pick' ? (
        <div className="space-y-3" data-testid="menu-import-step-pick">
          <div
            onDragOver={(ev) => {
              ev.preventDefault();
              setIsDragOver(true);
            }}
            onDragLeave={() => setIsDragOver(false)}
            onDrop={(ev) => {
              ev.preventDefault();
              setIsDragOver(false);
              void pickFile(ev.dataTransfer.files?.[0] ?? null);
            }}
            className={[
              'rounded-lg border-2 border-dashed p-8 text-center',
              isDragOver
                ? 'border-accent-500 bg-accent-50 dark:bg-accent-900/20'
                : 'border-border-secondary',
            ].join(' ')}
            data-testid="menu-import-dropzone"
          >
            <FileJson size={36} className="mx-auto text-text-tertiary" aria-hidden="true" />
            <p className="mt-3 text-sm text-text-primary">
              {previewMutation.isPending ? t(`${P}.checking`) : (fileName ?? t(`${P}.dropHint`))}
            </p>
            <label className="mt-3 inline-block cursor-pointer text-sm text-accent-700 hover:underline dark:text-accent-300">
              {/* Brak prymitywu file-input w bibliotece — ukryty natywny input jest tu kanoniczny
                  (ten sam wzorzec co AttachmentsSection i BugReportWidget). */}
              {/* eslint-disable-next-line no-restricted-syntax */}
              <input
                type="file"
                accept=".json,application/json"
                className="hidden"
                disabled={running}
                onChange={(ev) => {
                  void pickFile(ev.target.files?.[0] ?? null);
                  ev.target.value = '';
                }}
                data-testid="menu-import-file-input"
              />
              {t(`${P}.browse`)}
            </label>
          </div>
          {fileError ? (
            <Alert tone="error" data-testid="menu-import-file-error">
              {fileError}
            </Alert>
          ) : null}
        </div>
      ) : preview ? (
        <div className="space-y-4" data-testid="menu-import-step-preview">
          <Alert tone="info">{t(`${P}.notTransferredInfo`)}</Alert>

          <Input
            label={t(`${P}.nameLabel`)}
            hint={t(`${P}.nameHint`)}
            value={name}
            maxLength={120}
            onChange={(e) => {
              setName(e.target.value);
              setNameError(null);
            }}
            error={nameError ?? undefined}
            data-testid="menu-import-name"
          />

          <div className="flex flex-wrap gap-2" data-testid="menu-import-counts">
            <Badge tone="success">
              {t(`${P}.counts.loaded`, { n: preview.report.counts.loaded })}
            </Badge>
            {preview.report.counts.loadedWithoutLayout > 0 ? (
              <Badge tone="warning">
                {t(`${P}.counts.withoutLayout`, {
                  n: preview.report.counts.loadedWithoutLayout,
                })}
              </Badge>
            ) : null}
            <Badge tone={preview.report.counts.skipped > 0 ? 'danger' : 'neutral'}>
              {t(`${P}.counts.skipped`, { n: preview.report.counts.skipped })}
            </Badge>
          </div>

          {!preview.importable ? (
            <Alert tone="warning" data-testid="menu-import-nothing">
              {t(`${P}.errors.nothingToImport`)}
            </Alert>
          ) : null}

          {skipped.length > 0 ? (
            <section data-testid="menu-import-skipped">
              <h3 className="mb-1.5 text-sm font-semibold text-text-primary">
                {t(`${P}.sections.skipped`)}
              </h3>
              <ul className="space-y-1">
                {skipped.map((e, i) => (
                  <li key={`s-${i}`} className="text-sm">
                    <span className="font-medium text-text-primary">{entryLabel(e)}</span>
                    <span className="text-text-muted"> — {reasonText(e.reason)}</span>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {withoutLayout.length > 0 ? (
            <section data-testid="menu-import-without-layout">
              <h3 className="mb-1.5 text-sm font-semibold text-text-primary">
                {t(`${P}.sections.withoutLayout`)}
              </h3>
              <ul className="space-y-1">
                {withoutLayout.map((e, i) => (
                  <li key={`w-${i}`} className="text-sm">
                    <span className="font-medium text-text-primary">{entryLabel(e)}</span>
                    <span className="text-text-muted">
                      {' — '}
                      {(e.notes ?? []).map((n) => t(`${P}.notes.${n}`)).join(', ')}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {loaded.length > 0 ? (
            <section data-testid="menu-import-loaded">
              <h3 className="mb-1.5 text-sm font-semibold text-text-primary">
                {t(`${P}.sections.loaded`)}
              </h3>
              <ul className="space-y-1">
                {loaded.map((e, i) => (
                  <li key={`l-${i}`} className="text-sm text-text-secondary">
                    {entryLabel(e)}
                    {e.url ? (
                      // Odnośnik zewnętrzny — pokazujemy dokąd prowadzi (plik mógł go podmienić).
                      <span className="break-all text-text-muted" data-testid="menu-import-url">
                        {' — '}
                        {t(`${P}.externalLink`, { url: e.url })}
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </div>
      ) : null}
    </Modal>
  );
}
